import { VIDEO_BASE_URL } from '../config/api';

// --- BUNNY CDN CONFIGURATION ---
const BUNNY_STORAGE_ZONE = 'lobab-videos-storage';
const BUNNY_ACCESS_KEY = '17fc772c-a045-433f-8475f92a9a3b-eafa-4ffe';
const BUNNY_CDN_HOSTNAME = 'lobabschool.b-cdn.net';
// --------------------------------

/**
 * Upload an image file to Bunny Storage via the backend proxy.
 * @param {File} file - The image file to upload.
 * @returns {Promise<string>} - The public CDN URL of the uploaded image.
 */
export async function uploadImageToBunny(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64data = reader.result.split(',')[1];
      try {
        const res = await fetch(`${VIDEO_BASE_URL}/api/video/upload-thumbnail`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageBase64: base64data }),
        });

        if (!res.ok) throw new Error('Failed to upload image to Bunny');

        const data = await res.json();
        if (data.success && data.thumbnailUrl) {
          resolve(data.thumbnailUrl);
        } else {
          reject(new Error(data.error || 'فشل في رفع الصورة'));
        }
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('فشل في قراءة الملف'));
    reader.readAsDataURL(file);
  });
}

/**
 * Delete an image from Bunny Storage via the backend proxy.
 * @param {string} imageUrl - The full CDN URL of the image to delete.
 */
export async function deleteImageFromBunny(imageUrl) {
  if (!imageUrl) return;

  try {
    const res = await fetch(`${VIDEO_BASE_URL}/api/video/delete-thumbnail`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ thumbnailUrl: imageUrl }),
    });

    if (!res.ok) {
      console.warn('Failed to delete image from Bunny Storage server');
    }
    return await res.json();
  } catch (err) {
    console.error('Error during Bunny image deletion:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Upload a video file to Bunny Stream via the backend proxy.
 * @param {File} file - The video file to upload.
 * @param {Function} onProgress - Callback for upload progress (0-100).
 * @returns {Promise<string>} - The Video ID of the uploaded video.
 */
export async function uploadVideoToBunny(file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append('video', file);

    xhr.open('POST', `${VIDEO_BASE_URL}/api/video/upload`, true);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        const percent = Math.round((e.loaded / e.total) * 100);
        onProgress(percent);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          if (data.success && data.videoId) {
            resolve(data.videoId);
          } else {
            reject(new Error(data.error || 'فشل في رفع الفيديو'));
          }
        } catch (err) {
          reject(new Error('خطأ في معالجة استجابة الخادم'));
        }
      } else {
        reject(new Error(`خطأ في الخادم: ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('فشل في الاتصال بالخادم'));
    xhr.send(formData);
  });
}

/**
 * Upload a document (PDF) DIRECTLY to BunnyCDN Storage.
 * @param {File} file - The file to upload.
 * @param {Function} onProgress - Callback for upload progress (0-100).
 * @param {string} folder - Target folder in Bunny Storage (default: 'documents').
 * @returns {Promise<string>} - The final public CDN URL of the uploaded document.
 */
export async function uploadDocumentToBunny(file, onProgress, folder = 'documents') {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    // Create a unique and safe file name
    const timestamp = Date.now();
    const cleanFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const uniqueFileName = `${timestamp}_${cleanFileName}`;
    const encodedFolder = encodeURIComponent(folder);
    const url = `https://jh.storage.bunnycdn.com/${BUNNY_STORAGE_ZONE}/${encodedFolder}/${uniqueFileName}`;

    xhr.open('PUT', url, true);
    xhr.setRequestHeader('AccessKey', BUNNY_ACCESS_KEY);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const publicUrl = `https://${BUNNY_CDN_HOSTNAME}/${encodedFolder}/${uniqueFileName}`;
        resolve(publicUrl);
      } else {
        let errorMessage = 'فشل الرفع إلى الخادم';
        try {
          const errorResponse = JSON.parse(xhr.responseText);
          if (errorResponse.Message) errorMessage = errorResponse.Message;
        } catch (e) {
          // ignore parse error if response is not JSON
        }
        reject(new Error(`خطأ في الرفع (${xhr.status}): ${errorMessage}`));
      }
    };

    xhr.onerror = () => reject(new Error('فشل الاتصال بخادم BunnyCDN'));
    
    // Send the file directly as binary data
    xhr.send(file);
  });
}

/**
 * Upload an image DIRECTLY to BunnyCDN Storage into a specific folder.
 * Unlike uploadImageToBunny (which uses the backend proxy), this uploads
 * directly from the browser to Bunny Storage.
 * @param {File} file - The image file to upload.
 * @param {string} folder - Target folder in Bunny Storage.
 * @returns {Promise<string>} - The public CDN URL of the uploaded image.
 */
export async function uploadImageToBunnyDirect(file, folder) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    const timestamp = Date.now();
    const ext = file.name.split('.').pop() || 'jpg';
    const cleanName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const uniqueFileName = `${timestamp}_${cleanName}`;
    const encodedFolder = encodeURIComponent(folder);
    const url = `https://jh.storage.bunnycdn.com/${BUNNY_STORAGE_ZONE}/${encodedFolder}/${uniqueFileName}`;

    xhr.open('PUT', url, true);
    xhr.setRequestHeader('AccessKey', BUNNY_ACCESS_KEY);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const publicUrl = `https://${BUNNY_CDN_HOSTNAME}/${encodedFolder}/${uniqueFileName}`;
        resolve(publicUrl);
      } else {
        let errorMessage = 'فشل رفع الصورة';
        try {
          const errorResponse = JSON.parse(xhr.responseText);
          if (errorResponse.Message) errorMessage = errorResponse.Message;
        } catch (e) { /* ignore */ }
        reject(new Error(`خطأ في رفع الصورة (${xhr.status}): ${errorMessage}`));
      }
    };

    xhr.onerror = () => reject(new Error('فشل الاتصال بخادم BunnyCDN'));
    xhr.send(file);
  });
}
